import React, { useState } from 'react';
import {
  RocketLaunch as RocketIcon,
  Code as CodeIcon,
  DesignServices as DesignIcon,
  Devices as ResponsiveIcon,
  Storage as BackendIcon,
  ShoppingCart as EcommerceIcon,
  Description as CMSIcon,
  Bolt as BoltIcon,
  ArrowRight as ArrowRightIcon,
  CheckCircle as CheckCircleIcon,
  Palette as PaletteIcon,
  PhoneAndroid as MobileIcon,
  TrendingUp as ScalabilityIcon
} from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Container,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
  useTheme,
  alpha
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

const WebDevelopmentPage = () => {
  const theme = useTheme();
  const [hoveredCard, setHoveredCard] = useState(null);
  const navigate = useNavigate();

  const services = [
    {
      icon: <DesignIcon fontSize="large" />,
      title: "Website Design",
      description: "Beautiful, intuitive interfaces that enhance user engagement and reflect your brand identity.",
      image: "https://images.unsplash.com/photo-1547658719-da2b51169166?w=500&h=300&fit=crop"
    },
    {
      icon: <CodeIcon fontSize="large" />,
      title: "Front-End Development",
      description: "Pixel-perfect implementation of designs with clean, efficient code and modern frameworks.",
      image: "https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=500&h=300&fit=crop"
    },
    {
      icon: <BackendIcon fontSize="large" />,
      title: "Back-End Development",
      description: "Robust server-side solutions that power your website's functionality and performance.",
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=500&h=300&fit=crop"
    },
    {
      icon: <EcommerceIcon fontSize="large" />,
      title: "E-Commerce Solutions",
      description: "Complete online store setups with secure payment gateways and inventory management.",
      image: "https://images.unsplash.com/photo-1486401899868-0e435ed85128?w=500&h=300&fit=crop"
    },
    {
      icon: <CMSIcon fontSize="large" />,
      title: "Content Management Systems",
      description: "Easy-to-use CMS platforms that give you full control over your website content.",
      image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=500&h=300&fit=crop"
    }
  ];

  const benefits = [
    { icon: <BoltIcon />, title: "Expertise", description: "Deep understanding of web technologies and trends" },
    { icon: <PaletteIcon />, title: "Custom Solutions", description: "Tailored strategies for your specific needs" },
    { icon: <MobileIcon />, title: "User-Centric", description: "Engaging interfaces that delight users" },
    { icon: <ResponsiveIcon />, title: "Responsive", description: "Flawless performance on all devices" },
    { icon: <ScalabilityIcon />, title: "Scalability", description: "Websites that grow with your business" }
  ];

  return (
    <Box sx={{ bgcolor: 'background.default' }}>
      {/* Hero Section */}
      <Box sx={{
        position: 'relative',
        bgcolor: 'rgba(63, 81, 181, 0.9)',
        color: 'primary.contrastText',
        py: 12,
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.2)} 0%, ${alpha(theme.palette.primary.main, 0.2)} 100%)`,
          zIndex: 1
        }
      }}>
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Stack alignItems="center" textAlign="center" spacing={4}>
            <Chip
            onClick={()=>console.log("")}
              icon={<RocketIcon />}
              label="Crafting Digital Excellence"
              sx={{
                bgcolor: 'rgba(208, 227, 239, 0.55)',
                color: 'primary.contrastText',
                px: 2,
                py: 1,
                fontSize: '0.875rem'
              }}
            />

            <Typography variant="h2" component="h1" sx={{ fontWeight: 700, fontSize: { xs: '2.5rem', md: '3.5rem' } }}>
              Web <Box component="span" sx={{ color: '#69f0ae' }}>Development</Box>
            </Typography>

            <Typography variant="h5" sx={{ maxWidth: 800, opacity: 0.9 }}>
              Your online presence is often the first impression you make. We transform your digital dreams into stunning, functional websites.
            </Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Button
                variant="contained"
                color="secondary"
                size="large"
                onClick={() => navigate('/get-in-touch')}
                endIcon={<ArrowRightIcon />}
                sx={{
                  px: 4,
                  py: 1.5,
                  fontSize: '1rem',
                  '&:hover': {
                    transform: 'translateY(-2px)'
                  }
                }}
              >
                Get Started
              </Button>

              <Button
                variant="outlined"
                color="inherit"
                size="large"
                onClick={() => navigate('/services')}
                sx={{
                  px: 4,
                  py: 1.5,
                  fontSize: '1rem',
                  borderColor: alpha(theme.palette.common.white, 0.3),
                  color: 'primary.contrastText',
                  '&:hover': {
                    borderColor: theme.palette.common.white,
                    bgcolor: alpha(theme.palette.common.white, 0.1)
                  }
                }}
              >
                View Portfolio
              </Button>
            </Stack>
          </Stack>
        </Container>
      </Box>

      {/* Commitment Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={6} order={{ xs: 2, md: 1 }}>
            <Typography variant="h3" component="h2" sx={{ fontWeight: 700, mb: 3 }}>
              Our Web Development Commitment
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
              We take pride in our passion for web development. With skilled professionals and years of experience, we create websites that look exceptional and perform brilliantly.
            </Typography>
            <List dense>
              {[
                "Modern, responsive designs",
                "Performance-optimized code",
                "SEO-friendly architecture",
                "Secure development practices",
                "Ongoing support & maintenance"
              ].map((item, index) => (
                <ListItem key={index} disableGutters>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <CheckCircleIcon color="success" />
                  </ListItemIcon>
                  <ListItemText primary={item} />
                </ListItem>
              ))}
            </List>
          </Grid>
          <Grid item xs={12} md={6} order={{ xs: 1, md: 2 }}>
            <Box
              component="img"
              src="https://images.unsplash.com/photo-1467232004584-a241de8bcf5d?w=800&fit=crop"
              alt="Web Development"
              sx={{
                width: '100%',
                borderRadius: 2,
                boxShadow: theme.shadows[4],
                transition: 'transform 0.3s ease',
                '&:hover': {
                  transform: 'scale(1.02)'
                }
              }}
            />
          </Grid>
        </Grid>
      </Container>

      {/* Why Choose Us Section */}
      <Box sx={{ bgcolor: 'grey.50', py: 10 }}>
        <Stack spacing={6} alignItems="center" textAlign="center">
          <Typography
            variant="h3"
            component="h2"
            sx={{
              fontWeight: 800,
              color: 'primary.main',
              fontSize: { xs: '2rem', md: '2.75rem' },
            }}
          >
            Why Choose ShreeshTech?
          </Typography>



          <Grid container spacing={4} justifyContent="center">
            {benefits.map((benefit, index) => (
              <Grid
                item
                xs={12}
                sm={6}
                md={4}
                lg={3}
                key={index}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <Paper
                  elevation={3}
                  sx={{
                    p: 4,
                    width: '100%',
                    maxWidth: 280, // Ensures same width
                    minHeight: 260,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    borderRadius: 3,
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-6px)',
                      boxShadow: theme => theme.shadows[6],
                      bgcolor: theme => alpha(theme.palette.primary.light, 0.05),
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      bgcolor: 'primary.main',
                      color: 'primary.contrastText',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 32,
                      mb: 2,
                    }}
                  >
                    {benefit.icon}
                  </Box>

                  <Typography variant="h6" component="h3" sx={{ fontWeight: 600, mb: 1 }}>
                    {benefit.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {benefit.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

        </Stack>
      </Box>

      {/* Services Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Stack spacing={6} alignItems="center" textAlign="center">
          <Typography variant="h3" component="h2" sx={{ fontWeight: 700 }}>
            Our Web Development Services
          </Typography>

          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 700 }}>
            More than a service provider - we're your strategic partner in the digital landscape
          </Typography>

          <Grid container spacing={4} justifyContent="center">
            {services.map((service, index) => (
              <Grid item xs={12} sm={6} md={4} key={index} sx={{ display: 'flex' }}>
                <Card
                  elevation={hoveredCard === index ? 6 : 2}
                  onMouseEnter={() => setHoveredCard(index)}
                  onMouseLeave={() => setHoveredCard(null)}
                  sx={{
                    width: '100%',
                    maxWidth: 380,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)'
                    }
                  }}
                >
                  <CardMedia
                    component="img"
                    height="200"
                    image={service.image}
                    alt={service.title}
                    sx={{
                      objectFit: 'cover',
                      objectPosition: 'center'
                    }}
                  />
                  <CardContent sx={{
                    p: { xs: 3, md: 4 },
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column'
                  }}>
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        bgcolor: 'primary.light',
                        color: 'primary.contrastText',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 3,
                        mx: 'auto',
                        transition: 'all 0.3s ease',
                        ...(hoveredCard === index && {
                          transform: 'scale(1.1)',
                          bgcolor: 'secondary.main'
                        })
                      }}
                    >
                      {service.icon}
                    </Box>
                    <Typography variant="h5" component="h3" gutterBottom>
                      {service.title}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      {service.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Stack>
      </Container>

      {/* CTA Section */}
      <Box sx={{
        bgcolor: 'rgba(63, 81, 181, 0.9)',
        color: 'primary.contrastText',
        py: 10,
        backgroundImage: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.2)} 0%, ${alpha(theme.palette.primary.main, 0.2)} 100%)`
      }}>
        <Container maxWidth="lg">
          <Stack spacing={4} alignItems="center" textAlign="center">
            <Typography variant="h3" component="h2" sx={{ fontWeight: 700 }}>
              Ready to Build Your Digital Presence?
            </Typography>

            <Typography variant="h6" sx={{ maxWidth: 700, opacity: 0.9 }}>
              Your website is the foundation of your online identity. Let's create something remarkable together.
            </Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Button
                variant="contained"
                color="secondary"
                size="large"
                sx={{
                  px: 4,
                  py: 1.5,
                  fontSize: '1rem'
                }}
              >
                Start Your Project
              </Button>

              <Button
                variant="outlined"
                color="inherit"
                onClick={() => navigate('/get-in-touch')}
                size="large"
                sx={{
                  px: 4,
                  py: 1.5,
                  fontSize: '1rem',
                  borderColor: alpha(theme.palette.common.white, 0.3),
                  color: 'primary.contrastText',
                  '&:hover': {
                    borderColor: theme.palette.common.white,
                    bgcolor: 'rgba(63, 81, 181, 0.9)',
                  }
                }}
              >
                Get Free Quote
              </Button>
            </Stack>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
};

export default WebDevelopmentPage;